import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

const PendingApprovals = () => {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [showApproved, setShowApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showAadharModal, setShowAadharModal] = useState(false);
  const [selectedAadharUser, setSelectedAadharUser] = useState(null);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        console.log("  Current user:", user);
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
    fetchPendingUsers();
    fetchApprovedCaders();
  }, []);

  // Safe function to parse document paths
  const parseDocumentPaths = (documents) => {
    if (!documents) return [];
    
    if (Array.isArray(documents)) return documents;
    
    if (typeof documents === 'string') {
      try {
        const parsed = JSON.parse(documents);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Error parsing document paths:", e);
        return [];
      }
    }
    
    return [];
  };

  // Safe function to get document count
  const getDocumentCount = (user) => {
    if (user.documents && Array.isArray(user.documents)) {
      return user.documents.length;
    }
    if (user.document_paths) {
      const docs = parseDocumentPaths(user.document_paths);
      return docs.length;
    }
    return 0;
  };

  // Fetch pending registrations - CADER endpoint only
  const fetchPendingUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem('token');
      
      const url = `${API_BASE}/admin/pending-caders`;
      
      console.log(`📡 Fetching pending cader registrations from:`, url);
      
      const response = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });
      
      console.log("Received response:", response.data);
      
      const users = response.data.caders || [];
      
      const processedUsers = users.map(user => ({
        ...user,
        documents: parseDocumentPaths(user.document_paths || user.documents),
        document_count: getDocumentCount(user),
        aadhar_image_path: user.aadhar_image_path || null,
        aadhar_number: user.aadhar_number || null,
        aadhar_verified: user.aadhar_verified === 1 || user.aadhar_verified === true,
        aadhar_verified_at: user.aadhar_verified_at || null
      }));
      
      setPendingUsers(processedUsers);
      setDebugInfo(null);
      
      if (processedUsers.length === 0) {
        console.log("ℹ️ No pending cader registrations found");
      }
      
    } catch (error) {
      console.error("❌ Error fetching pending users:", error);
      
      let errorMessage = "Failed to fetch pending registrations";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timeout - server not responding";
      } else if (error.response) {
        errorMessage = error.response.data?.error || `Server error: ${error.response.status}`;
        console.error("Error response data:", error.response.data);
      } else if (error.request) {
        errorMessage = "Cannot connect to server. Please check your connection.";
      }
      
      setError(errorMessage);
      setDebugInfo({
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch approved caders
  const fetchApprovedCaders = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const url = `${API_BASE}/admin/all-caders`;
      
      console.log(`📡 Fetching approved caders from:`, url);
      
      const response = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });
      
      console.log("✅ Approved caders response:", response.data);
      
      const users = response.data.caders || [];
      
      // Filter only approved/active caders
      const approvedOnly = users.filter(user => 
        user.status === 'active' || user.status === 'approved'
      );
      
      const processedUsers = approvedOnly.map(user => ({
        ...user,
        documents: parseDocumentPaths(user.document_paths || user.documents),
        document_count: getDocumentCount(user),
        aadhar_image_path: user.aadhar_image_path || null,
        aadhar_number: user.aadhar_number || null,
        aadhar_verified: user.aadhar_verified === 1 || user.aadhar_verified === true,
        aadhar_verified_at: user.aadhar_verified_at || null,
        approved_at: user.approved_at || user.updated_at
      }));
      
      setApprovedUsers(processedUsers);
      
    } catch (error) {
      console.error("❌ Error fetching approved caders:", error);
    }
  };

  // Delete cader
  const handleDeleteCader = async (user) => {
    if (!window.confirm(`⚠️ Are you sure you want to delete Cader "${user.full_name}" (${user.username})?\n\nThis action cannot be undone!`)) return;
    
    setProcessingId(user.id);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token');
      
      console.log("📤 Sending delete request for cader:", user.username);
      
      const response = await axios.delete(`${API_BASE}/admin/delete-user?username=${user.username}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log("✅ Delete response:", response.data);
      
      if (response.data.success) {
        alert(`✅ Cader "${user.full_name}" has been deleted successfully.`);
        fetchPendingUsers();
        fetchApprovedCaders();
      } else {
        alert(`❌ Error: ${response.data.error || 'Failed to delete cader'}`);
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      const errorMsg = error.response?.data?.error || 
                      error.message || 
                      "Failed to delete cader";
      alert(`❌ ${errorMsg}`);
      setError(errorMsg);
    } finally {
      setProcessingId(null);
    }
  };

  // Download Aadhar card using fetch API
  const downloadAadharCard = async (user) => {
    if (!user.aadhar_image_path) {
      alert("No Aadhar card image found");
      return;
    }
    
    setDownloadingId(`aadhar_${user.id}`);
    
    try {
      const token = sessionStorage.getItem('token');
      const filePath = user.aadhar_image_path;
      
      console.log("📥 Downloading Aadhar card from:", filePath);
      
      const filename = `Aadhar_${user.username}_${user.aadhar_number || 'card'}.${filePath.split('.').pop() || 'jpg'}`;
      const downloadUrl = `${API_BASE}/admin/download-document?path=${encodeURIComponent(filePath)}&filename=${encodeURIComponent(filename)}&token=${token}`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log("✅ Aadhar card downloaded successfully");
      
    } catch (error) {
      console.error("❌ Aadhar download error:", error);
      alert("Failed to download Aadhar card. Please try again.");
    } finally {
      setTimeout(() => {
        setDownloadingId(null);
      }, 1000);
    }
  };

  // View Aadhar card
  const viewAadharCard = (user) => {
    console.log("🆔 Viewing Aadhar card for:", user.full_name);
    setSelectedAadharUser(user);
    setShowAadharModal(true);
  };

  // Verify Aadhar
  const handleVerifyAadhar = async (user, verified) => {
    if (!window.confirm(`Verify Aadhar card for ${user.full_name}? This will mark it as ${verified ? 'verified' : 'rejected'}.`)) return;
    
    setProcessingId(user.id);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token');
      
      const requestData = { 
        cader_id: user.id,
        approver_id: currentUser?.id || null,
        verified: verified
      };
      
      console.log("📤 Sending Aadhar verification request:", requestData);
      
      const response = await axios.post(`${API_BASE}/admin/verify-cader-aadhar`, 
        requestData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log("✅ Aadhar verification response:", response.data);
      
      if (response.data.success) {
        alert(`✅ Aadhar card ${verified ? 'verified' : 'rejected'} for ${user.full_name}`);
        fetchPendingUsers();
        fetchApprovedCaders();
        if (showAadharModal) {
          setShowAadharModal(false);
          setSelectedAadharUser(null);
        }
      } else {
        alert(`❌ Error: ${response.data.error || response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("❌ Aadhar verification error:", error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      "Failed to verify Aadhar";
      alert(`❌ ${errorMsg}`);
      setError(errorMsg);
    } finally {
      setProcessingId(null);
    }
  };

  // Handle approve for CADER
  const handleApprove = async (user) => {
    if (!user.aadhar_verified) {
      alert(`⚠️ Please verify Aadhar card for ${user.full_name} before approving.`);
      return;
    }
    
    if (!window.confirm(`Approve Cader ${user.full_name}? This will activate their account.`)) return;
    
    setProcessingId(user.id);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token');
      
      const requestData = { 
        cader_id: user.id,
        approver_id: currentUser?.id || null
      };
      
      console.log("📤 Sending approve request for cader:", requestData);
      
      const response = await axios.post(`${API_BASE}/admin/approve-cader`, 
        requestData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log("✅ Approve response:", response.data);
      
      if (response.data.success) {
        alert(`✅ ${user.full_name} approved! Cader account activated.`);
        fetchPendingUsers();
        fetchApprovedCaders();
      } else {
        alert(`❌ Error: ${response.data.error || response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("❌ Approve error:", error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      "Failed to approve cader";
      alert(`❌ ${errorMsg}`);
      setError(errorMsg);
    } finally {
      setProcessingId(null);
    }
  };

  // Handle reject for CADER
  const handleReject = async (user) => {
    const reason = prompt(`Reject Cader ${user.full_name}? Enter reason:`);
    if (reason === null) return;
    
    setProcessingId(user.id);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token');
      
      const requestData = { 
        cader_id: user.id,
        approver_id: currentUser?.id || null,
        reason: reason?.trim() || "No reason provided"
      };
      
      console.log("📤 Sending reject request for cader:", requestData);
      
      const response = await axios.post(`${API_BASE}/admin/reject-cader`, 
        requestData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      console.log("✅ Reject response:", response.data);
      
      if (response.data.success) {
        alert(`❌ ${user.full_name} rejected`);
        fetchPendingUsers();
        fetchApprovedCaders();
      } else {
        alert(`❌ Error: ${response.data.error || response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("❌ Reject error:", error);
      const errorMsg = error.response?.data?.error || 
                      error.message || 
                      "Failed to reject cader";
      alert(`❌ ${errorMsg}`);
      setError(errorMsg);
    } finally {
      setProcessingId(null);
    }
  };

  // View documents
  const viewDocuments = (user) => {
    console.log("📎 Viewing documents for:", user.full_name);
    
    let docs = [];
    if (user.documents && Array.isArray(user.documents)) {
      docs = user.documents;
    } else if (user.document_paths) {
      docs = parseDocumentPaths(user.document_paths);
    }
    
    setSelectedDocuments({
      ...user,
      documents: docs
    });
    setShowDocumentModal(true);
  };

  // Download document using fetch API
  const downloadDocument = async (doc) => {
    const filePath = doc.file_path || doc.path || '';
    const filename = doc.original_filename || doc.original_name || 'document';
    const docId = doc.id || doc.filename || Math.random();
    
    if (!filePath) {
      alert("No file path available for this document");
      return;
    }
    
    setDownloadingId(docId);
    
    try {
      const token = sessionStorage.getItem('token');
      
      const downloadUrl = `${API_BASE}/admin/download-document?path=${encodeURIComponent(filePath)}&filename=${encodeURIComponent(filename)}&token=${token}`;
      
      console.log("📥 Downloading from:", downloadUrl);
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log("✅ Download completed for:", filename);
      
    } catch (error) {
      console.error("❌ Download error:", error);
      alert(`Failed to download document: ${error.message}`);
    } finally {
      setTimeout(() => setDownloadingId(null), 1000);
    }
  };

  // Get role name
  const getRoleName = (roleId) => {
    const roles = {
      1: "ULB User",
      2: "District Admin",
      3: "Divisional Officer",
      4: "DMA Desk Officer",
      5: "Super Admin",
      6: "DMA Desk User",
      7: "ULB Chief Officer",
      8: "DMA Admin",
      9: "DMA System Admin",
      10: "Support User",
      11: "Cader"
    };
    return roles[roleId] || `Role ${roleId}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Mask Aadhar number
  const maskAadhar = (aadhar) => {
    if (!aadhar) return 'Not provided';
    if (aadhar.length === 12) {
      return `XXXX-XXXX-${aadhar.slice(-4)}`;
    }
    return aadhar;
  };

  // Render user table
  const renderUserTable = (users, title, showDeleteButton = false) => {
    if (users.length === 0) {
      return (
        <div style={{ 
          textAlign: "center", 
          padding: "40px",
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>📋</div>
          <h3 style={{ color: "#2c3e50", marginBottom: "10px" }}>No {title} found</h3>
          <p style={{ color: "#6c757d" }}>
            {title === "Approved Cader" ? "No approved caders yet." : "No pending registrations."}
          </p>
        </div>
      );
    }

    return (
      <div style={{
        background: "white",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        overflow: "auto",
        marginBottom: "30px"
      }}>
        <h3 style={{ padding: "20px 20px 0 20px", margin: 0, color: "#0d47a1" }}>
          {title} ({users.length})
        </h3>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px" }}>
          <thead style={{ background: "#f8f9fa" }}>
            <tr>
              <th style={{ padding: "15px", textAlign: "left" }}>User Details</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Contact</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Phone</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Aadhar</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Designation</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Location</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Documents</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Created/Approved On</th>
              <th style={{ padding: "15px", textAlign: "left" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: "1px solid #e9ecef" }}>
                <td style={{ padding: "15px" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "16px" }}>{user.full_name}</div>
                    <div style={{ fontSize: "13px", color: "#6c757d" }}>
                      @{user.username}
                    </div>
                    <div style={{ fontSize: "12px", color: "#0d47a1", marginTop: "4px" }}>
                      Role: {getRoleName(user.role_id || 11)}
                    </div>
                  </div>
                </td>
                <td style={{ padding: "15px" }}>
                  <div style={{ fontSize: "13px", color: "#495057" }}>
                    {user.email}
                  </div>
                </td>
                <td style={{ padding: "15px" }}>
                  {user.phone_no ? (
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "5px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#0d47a1"
                    }}>
                      📞 {user.phone_no}
                    </div>
                  ) : (
                    <span style={{ color: "#6c757d", fontSize: "12px" }}>Not provided</span>
                  )}
                </td>
                <td style={{ padding: "15px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ fontSize: "13px", fontWeight: "500" }}>
                      {maskAadhar(user.aadhar_number)}
                    </div>
                    {user.aadhar_image_path && (
                      <div style={{ display: "flex", gap: "5px" }}>
                        <button
                          onClick={() => viewAadharCard(user)}
                          style={{
                            padding: "4px 8px",
                            background: user.aadhar_verified ? "#28a745" : "#02ac10",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px"
                          }}
                        >
                          {user.aadhar_verified ? "✓ Verified" : "🔍 View"}
                        </button>
                        <button
                          onClick={() => downloadAadharCard(user)}
                          disabled={downloadingId === `aadhar_${user.id}`}
                          style={{
                            padding: "4px 8px",
                            background: "#074eaa",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            cursor: downloadingId === `aadhar_${user.id}` ? "not-allowed" : "pointer",
                            opacity: downloadingId === `aadhar_${user.id}` ? 0.6 : 1
                          }}
                        >
                          {downloadingId === `aadhar_${user.id}` ? "⏳..." : "📥 Download"}
                        </button>
                      </div>
                    )}
                  </div>
                </td>
                <td style={{ padding: "15px" }}>
                  {user.designation ? (
                    <span style={{
                      padding: "4px 10px",
                      background: "#e8f5e9",
                      color: "#2e7d32",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: "500",
                      display: "inline-block"
                    }}>
                      💼 {user.designation}
                    </span>
                  ) : (
                    <span style={{ color: "#6c757d", fontSize: "12px" }}>Not specified</span>
                  )}
                </td>
                <td style={{ padding: "15px" }}>
                  <div style={{ fontSize: "13px" }}>
                    {user.state_name && <div>📍 {user.state_name}</div>}
                    {user.division_name && <div>📌 Division: {user.division_name}</div>}
                    {user.district_name && <div>🏢 District: {user.district_name}</div>}
                  </div>
                </td>
                <td style={{ padding: "15px" }}>
                  <button
                    onClick={() => viewDocuments(user)}
                    style={{
                      padding: "6px 12px",
                      background: "#17a2b8",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px"
                    }}
                  >
                    📎 View ({getDocumentCount(user)})
                  </button>
                </td>
                <td style={{ padding: "15px", fontSize: "13px" }}>
                  {formatDate(user.approved_at || user.created_at || user.requested_at)}
                </td>
                <td style={{ padding: "15px" }}>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                    {/* Show Approve/Reject buttons only for Pending view */}
                    {!showDeleteButton && (
                      <>
                        {/* Aadhar verification buttons for pending users */}
                        {!user.aadhar_verified && user.aadhar_image_path && (
                          <>
                            <button
                              onClick={() => handleVerifyAadhar(user, true)}
                              disabled={processingId === user.id}
                              style={{
                                padding: "4px 6px",
                                background: "#28a745",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "11px",
                                cursor: processingId === user.id ? "not-allowed" : "pointer",
                                opacity: processingId === user.id ? 0.6 : 1,
                                whiteSpace: "nowrap"
                              }}
                            >
                              Verify Aadhar
                            </button>
                            <button
                              onClick={() => handleVerifyAadhar(user, false)}
                              disabled={processingId === user.id}
                              style={{
                                padding: "4px 6px",
                                background: "#dc3545",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "11px",
                                cursor: processingId === user.id ? "not-allowed" : "pointer",
                                opacity: processingId === user.id ? 0.6 : 1,
                                whiteSpace: "nowrap"
                              }}
                            >
                              Reject Aadhar
                            </button>
                          </>
                        )}
                        
                        {/* Approve button - only enabled if Aadhar is verified */}
                        <button
                          onClick={() => handleApprove(user)}
                          disabled={processingId === user.id || !user.aadhar_verified}
                          style={{
                            padding: "4px 6px",
                            background: "#28a745",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            cursor: (processingId === user.id || !user.aadhar_verified) ? "not-allowed" : "pointer",
                            opacity: (processingId === user.id || !user.aadhar_verified) ? 0.6 : 1,
                            minWidth: "65px",
                            whiteSpace: "nowrap"
                          }}
                          title={!user.aadhar_verified ? "Verify Aadhar first" : ""}
                        >
                          {processingId === user.id ? "⏳" : "✓ Approve"}
                        </button>
                        
                        {/* Reject button */}
                        <button
                          onClick={() => handleReject(user)}
                          disabled={processingId === user.id}
                          style={{
                            padding: "4px 6px",
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            cursor: processingId === user.id ? "not-allowed" : "pointer",
                            opacity: processingId === user.id ? 0.6 : 1,
                            minWidth: "65px",
                            whiteSpace: "nowrap"
                          }}
                        >
                          ✗ Reject
                        </button>
                      </>
                    )}
                    
                    {/* Show Delete button only for Approved view */}
                    {showDeleteButton && (
                      <button
                        onClick={() => handleDeleteCader(user)}
                        disabled={processingId === user.id}
                        style={{
                          padding: "4px 6px",
                          background: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          fontSize: "11px",
                          cursor: processingId === user.id ? "not-allowed" : "pointer",
                          opacity: processingId === user.id ? 0.6 : 1,
                          minWidth: "65px",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {processingId === user.id ? "⏳" : "🗑 Delete"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading && !showApproved) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh" 
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
          <div style={{ fontSize: "18px", color: "#2c3e50" }}>Loading pending approvals...</div>
          <div style={{ fontSize: "14px", color: "#6c757d", marginTop: "10px" }}>
            Fetching Cader registration requests
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "30px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "30px",
        background: "white",
        padding: "20px",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
      }}>
        <div>
          <h1 style={{ color: "#2c3e50", margin: "0 0 5px 0" }}>
            {showApproved ? "✅ Approved Cader List" : " Pending Cader Approvals"}
          </h1>
          <p style={{ color: "#6c757d", margin: 0 }}>
            {showApproved 
              ? `${approvedUsers.length} approved cader${approvedUsers.length !== 1 ? 's' : ''} in the system`
              : `${pendingUsers.length} cader registration${pendingUsers.length !== 1 ? 's' : ''} waiting for approval`}
          </p>
        </div>
        
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => {
              setShowApproved(!showApproved);
              if (showApproved) {
                fetchPendingUsers();
              }
            }}
            style={{
              padding: "10px 20px",
              background: showApproved ? "#e47c27" : "#28a745",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px"
            }}
          >
            {showApproved ? "View Pending" : "View Approved"}
          </button>
          <button
            onClick={() => {
              fetchPendingUsers();
              fetchApprovedCaders();
            }}
            style={{
              padding: "10px 20px",
              background: "#0d47a1",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px"
            }}
          >
            <span>🔄</span> Refresh
          </button>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "10px 20px",
              background: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer"
            }}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Error message if any */}
      {error && (
        <div style={{
          padding: "15px",
          background: "#f8d7da",
          border: "1px solid #f5c6cb",
          borderRadius: "8px",
          color: "#721c24",
          marginBottom: "20px"
        }}>
          <strong>Error:</strong> {error}
          {debugInfo && (
            <pre style={{ fontSize: "12px", marginTop: "10px", overflow: "auto" }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Render appropriate table based on showApproved state */}
      {showApproved 
        ? renderUserTable(approvedUsers, "Approved Cader", true)
        : renderUserTable(pendingUsers, "Pending Cader", false)}

      {/* Aadhar Card Modal */}
      {showAadharModal && selectedAadharUser && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "30px",
            maxWidth: "500px",
            width: "90%",
            maxHeight: "80vh",
            overflow: "auto"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px"
            }}>
              <div>
                <h3 style={{ margin: 0 }}>
                  🆔 Aadhar Card - {selectedAadharUser.full_name}
                </h3>
                <p style={{ margin: "5px 0 0 0", fontSize: "13px", color: "#6c757d" }}>
                  Number: {maskAadhar(selectedAadharUser.aadhar_number)}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAadharModal(false);
                  setSelectedAadharUser(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6c757d"
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              background: "#f8f9fa",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "20px",
              textAlign: "center"
            }}>
              {selectedAadharUser.aadhar_image_path ? (
                <div>
                  <div style={{ fontSize: "64px", marginBottom: "10px" }}>
                    {selectedAadharUser.aadhar_image_path.toLowerCase().includes('pdf') ? '📄' : '🖼️'}
                  </div>
                  <div style={{ marginTop: "10px", display: "flex", gap: "10px", justifyContent: "center" }}>
                    <button
                      onClick={() => downloadAadharCard(selectedAadharUser)}
                      disabled={downloadingId === `aadhar_${selectedAadharUser.id}`}
                      style={{
                        padding: "8px 16px",
                        background: "#0d47a1",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: downloadingId === `aadhar_${selectedAadharUser.id}` ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        opacity: downloadingId === `aadhar_${selectedAadharUser.id}` ? 0.6 : 1
                      }}
                    >
                      {downloadingId === `aadhar_${selectedAadharUser.id}` ? "⏳" : "📥"} Download
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ color: "#6c757d" }}>
                  No Aadhar card image uploaded
                </div>
              )}
            </div>

            <div style={{
              padding: "15px",
              background: "#e3f2fd",
              borderRadius: "8px",
              marginBottom: "20px"
            }}>
              <div style={{ fontSize: "14px", marginBottom: "5px" }}>
                <strong> User Information:</strong>
              </div>
              <div style={{ fontSize: "13px" }}>
                <div> Name: {selectedAadharUser.full_name}</div>
                <div> Email: {selectedAadharUser.email}</div>
                <div> Phone: {selectedAadharUser.phone_no || 'Not provided'}</div>
                <div> Designation: {selectedAadharUser.designation || 'Not specified'}</div>
                <div> Location: {selectedAadharUser.district_name || 'N/A'}, {selectedAadharUser.state_name || 'N/A'}</div>
              </div>
            </div>

            {!showApproved && (
              <div style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px"
              }}>
                {!selectedAadharUser.aadhar_verified && (
                  <>
                    <button
                      onClick={() => handleVerifyAadhar(selectedAadharUser, true)}
                      disabled={processingId === selectedAadharUser.id}
                      style={{
                        padding: "10px 20px",
                        background: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: processingId === selectedAadharUser.id ? "not-allowed" : "pointer",
                        opacity: processingId === selectedAadharUser.id ? 0.6 : 1
                      }}
                    >
                      ✓ Verify Aadhar
                    </button>
                    <button
                      onClick={() => handleVerifyAadhar(selectedAadharUser, false)}
                      disabled={processingId === selectedAadharUser.id}
                      style={{
                        padding: "10px 20px",
                        background: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: processingId === selectedAadharUser.id ? "not-allowed" : "pointer",
                        opacity: processingId === selectedAadharUser.id ? 0.6 : 1
                      }}
                    >
                      ✗ Reject Aadhar
                    </button>
                  </>
                )}
                {selectedAadharUser.aadhar_verified && (
                  <div style={{
                    padding: "10px 20px",
                    background: "#d4edda",
                    color: "#155724",
                    borderRadius: "4px",
                    textAlign: "center"
                  }}>
                    ✓ Aadhar Verified on {formatDate(selectedAadharUser.aadhar_verified_at)}
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowAadharModal(false);
                    setSelectedAadharUser(null);
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showDocumentModal && selectedDocuments && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "30px",
            maxWidth: "600px",
            width: "90%",
            maxHeight: "80vh",
            overflow: "auto"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px"
            }}>
              <div>
                <h3 style={{ margin: 0 }}>
                  📎 Documents - {selectedDocuments.full_name}
                </h3>
                {selectedDocuments.designation && (
                  <p style={{ margin: "5px 0 0 0", fontSize: "13px", color: "#2e7d32" }}>
                     {selectedDocuments.designation}
                  </p>
                )}
                {selectedDocuments.phone_no && (
                  <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#0d47a1" }}>
                     {selectedDocuments.phone_no}
                  </p>
                )}
                {selectedDocuments.aadhar_number && (
                  <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#ff9800" }}>
                     Aadhar: {maskAadhar(selectedDocuments.aadhar_number)}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowDocumentModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6c757d"
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <p style={{ color: "#6c757d", margin: 0 }}>
                {selectedDocuments.documents?.length || 0} document(s) uploaded
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {selectedDocuments.documents?.map((doc, index) => (
                <div
                  key={doc.id || index}
                  style={{
                    padding: "15px",
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>
                      {doc.original_filename || doc.original_name || `Document ${index + 1}`}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      {doc.file_size ? (doc.file_size / 1024).toFixed(2) : '?'} KB • 
                      {doc.file_type?.split('/').pop() || doc.mime_type?.split('/').pop() || 'Unknown'}
                    </div>
                  </div>
                  <button
                    onClick={() => downloadDocument(doc)}
                    disabled={downloadingId === (doc.id || index)}
                    style={{
                      padding: "6px 12px",
                      background: "#0d47a1",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: downloadingId === (doc.id || index) ? "not-allowed" : "pointer",
                      opacity: downloadingId === (doc.id || index) ? 0.6 : 1,
                      marginLeft: "10px",
                      minWidth: "80px"
                    }}
                  >
                    {downloadingId === (doc.id || index) ? "⏳..." : "⬇ Download"}
                  </button>
                </div>
              ))}
            </div>

            {(!selectedDocuments.documents || selectedDocuments.documents.length === 0) && (
              <div style={{
                textAlign: "center",
                padding: "40px",
                color: "#6c757d"
              }}>
                📄 No documents uploaded
              </div>
            )}

            <div style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px"
            }}>
              <button
                onClick={() => setShowDocumentModal(false)}
                style={{
                  padding: "10px 20px",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApprovals;